<template>
  <div>
    <el-card class="statistics-card" shadow="never" v-loading="loading" element-loading-text="正在加载评论数据">
      <div slot="header" class="card-header">
        <div>
          <div class="card-title">各平台评论总量</div>
          <div class="card-description">对比各平台作品累计评论数，查看较前一日的增减。</div>
        </div>
        <el-button v-if="error" type="text" :disabled="loading || !date" @click="load(date, platform)">重试</el-button>
      </div>
      <el-alert
        v-if="error"
        class="statistics-alert"
        type="error"
        :closable="false"
        show-icon
        title="评论数据加载失败"
        description="请稍后重试。"
      />
      <el-alert
        v-else-if="incompleteNotice"
        class="statistics-alert"
        type="warning"
        :closable="false"
        show-icon
        :title="incompleteNotice"
      />
      <el-table
        :data="items"
        empty-text="该日期暂无作品运营数据，请更换日期"
        class="summary-table"
      >
        <el-table-column label="平台" min-width="110">
          <template slot-scope="scope"><platform-tag :value="scope.row.platform" /></template>
        </el-table-column>
        <el-table-column :label="currentColumnLabel" min-width="145" align="right">
          <template slot-scope="scope">
            <div class="total-value">{{ totalText(scope.row, 'current_total') }}</div>
            <div v-if="totalNote(scope.row, 'current_total')" class="total-note">{{ totalNote(scope.row, 'current_total') }}</div>
          </template>
        </el-table-column>
        <el-table-column :label="previousColumnLabel" min-width="145" align="right">
          <template slot-scope="scope">
            <div class="total-value">{{ totalText(scope.row, 'previous_total') }}</div>
            <div v-if="totalNote(scope.row, 'previous_total')" class="total-note">{{ totalNote(scope.row, 'previous_total') }}</div>
          </template>
        </el-table-column>
        <el-table-column label="变化" min-width="150" align="right">
          <template slot-scope="scope">
            <span v-if="scope.row.net_change === null" class="muted">—</span>
            <span v-else :class="changeClass(scope.row.net_change)">
              {{ signed(scope.row.net_change) }}
              <span class="change-rate">{{ rate(scope.row.change_rate) }}</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="数据完整度" min-width="210">
          <template slot-scope="scope">
            <div :class="coverageClass(scope.row)">{{ coverageText(scope.row) }}</div>
            <div v-if="statusNote(scope.row)" class="status-note">{{ statusNote(scope.row) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="190" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.calculation_status !== 'no_data'" type="text" @click="openDetails(scope.row)">{{ detailActionText(scope.row) }}</el-button>
            <span v-else class="muted">—</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-drawer
      :title="detailDrawerTitle"
      :visible.sync="drawerVisible"
      size="78%"
      :before-close="closeDrawer"
    >
      <div class="drawer-body" v-loading="detailsLoading" element-loading-text="正在加载作品变化明细">
        <el-form inline class="detail-filters" @submit.native.prevent>
          <el-form-item label="日期">
            <el-date-picker v-model="detailDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" />
          </el-form-item>
          <el-form-item label="平台">
            <el-select v-model="detailPlatform" clearable placeholder="全部平台">
              <el-option v-for="option in platforms" :key="option.value" :label="option.label" :value="option.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="变化类型">
            <el-select v-model="changeType" placeholder="全部变化">
              <el-option label="全部变化" value="all" />
              <el-option label="评论增长" value="increased" />
              <el-option label="评论下降" value="decreased" />
              <el-option label="没有变化" value="unchanged" />
              <el-option label="当日新作品" value="new_content" />
              <el-option label="统计范围新增" value="baseline_added" />
              <el-option label="当日缺少数据" value="current_missing" />
              <el-option label="前一日缺少数据" value="previous_missing" />
              <el-option label="数据缺失" value="anomaly" />
            </el-select>
          </el-form-item>
          <el-form-item label="作品">
            <el-input v-model.trim="keyword" clearable placeholder="搜索标题或作品ID" @keyup.enter.native="searchDetails" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="detailsLoading" :disabled="detailsLoading" @click="searchDetails">查询</el-button>
          </el-form-item>
        </el-form>
        <el-alert
          v-if="detailFilterHint"
          class="statistics-alert"
          type="info"
          :closable="false"
          show-icon
          title="已定位到相关作品"
          :description="detailFilterHint"
        />
        <el-alert
          v-if="detailsError"
          class="statistics-alert"
          type="error"
          :closable="false"
          show-icon
          title="作品明细加载失败"
          description="请稍后重试。"
        />
        <el-table :data="details" empty-text="当前条件下没有作品变化数据" class="details-table">
          <el-table-column label="作品" min-width="260">
            <template slot-scope="scope">
              <a v-if="isURL(scope.row.source_url)" :href="scope.row.source_url" target="_blank" @click.stop>{{ scope.row.title || scope.row.source_id }}</a>
              <span v-else>{{ scope.row.title || scope.row.source_id }}</span>
              <div class="muted source-id">{{ scope.row.source_id }}</div>
            </template>
          </el-table-column>
          <el-table-column label="平台" width="100">
            <template slot-scope="scope"><platform-tag :value="scope.row.platform" /></template>
          </el-table-column>
          <el-table-column label="发布时间" width="150">
            <template slot-scope="scope">{{ formatDate(scope.row.published_at) }}</template>
          </el-table-column>
          <el-table-column label="纳入监测时间" width="150">
            <template slot-scope="scope">{{ formatDate(scope.row.first_monitored_at) }}</template>
          </el-table-column>
          <el-table-column :label="detailCurrentColumnLabel" width="110" align="right">
            <template slot-scope="scope">{{ nullableNumber(scope.row.current_count) }}</template>
          </el-table-column>
          <el-table-column :label="detailPreviousColumnLabel" width="110" align="right">
            <template slot-scope="scope">{{ previousText(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="变化量" width="95" align="right">
            <template slot-scope="scope">
              <span v-if="scope.row.change_value !== null" :class="changeClass(scope.row.change_value)">{{ signed(scope.row.change_value) }}</span>
              <span v-else class="muted">-</span>
            </template>
          </el-table-column>
          <el-table-column label="数据状态" min-width="150">
            <template slot-scope="scope">
              <el-tag size="mini" :type="detailStatusType(scope.row)">{{ detailStatusText(scope.row) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="已采集评论" width="110" align="right">
            <template slot-scope="scope">{{ number(scope.row.collected_comment_count) }}</template>
          </el-table-column>
          <el-table-column label="数据更新时间" width="150">
            <template slot-scope="scope">{{ formatDate(scope.row.current_captured_at) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" @click="viewComments(scope.row)">查看评论</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination">
          <el-pagination
            background
            layout="total, prev, pager, next, jumper"
            :current-page="detailPage"
            :page-size="detailPageSize"
            :total="detailTotal"
            @current-change="changeDetailPage"
          />
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'
import PlatformTag from '../components/PlatformTag.vue'

export default {
  components: { PlatformTag },
  data () {
    return {
      date: '',
      previousDate: '',
      platform: '',
      items: [],
      loading: false,
      error: '',
      drawerVisible: false,
      detailDate: '',
      detailPreviousDate: '',
      detailPlatform: '',
      changeType: 'all',
      keyword: '',
      details: [],
      detailsLoading: false,
      detailsError: '',
      detailPage: 1,
      detailPageSize: 20,
      detailTotal: 0,
      platforms: [
        { value: 'douyin', label: '抖音' },
        { value: 'bilibili', label: 'B站' },
        { value: 'xiaohongshu', label: '小红书' },
        { value: 'wechat_channels', label: '视频号' },
        { value: 'wechat_mp', label: '公众号' }
      ]
    }
  },
  computed: {
    currentColumnLabel () {
      return `${this.shortDate(this.date)} 评论总数`
    },
    previousColumnLabel () {
      return `${this.shortDate(this.previousDate)} 评论总数`
    },
    detailCurrentColumnLabel () {
      return `${this.shortDate(this.detailDate)} 评论`
    },
    detailPreviousColumnLabel () {
      return `${this.shortDate(this.detailPreviousDate)} 评论`
    },
    detailDrawerTitle () {
      const platform = this.platformName(this.detailPlatform)
      const count = this.detailTotal ? `（${this.detailTotal}篇）` : ''
      if (this.changeType === 'current_missing') return `${platform} · ${this.shortDate(this.detailDate)} 缺少数据的作品${count}`
      if (this.changeType === 'previous_missing') return `${platform} · ${this.shortDate(this.detailPreviousDate)} 缺少数据的作品${count}`
      if (this.changeType === 'baseline_added') return `${platform} · 作品统计范围变化${count}`
      return `${platform} · 作品评论变化明细${count}`
    },
    incompleteNotice () {
      if (!this.items.length) return ''
      const missingSnapshots = this.items.filter(item => {
        return Number(item.missing_current_count || 0) > 0 || Number(item.missing_previous_count || 0) > 0
      })
      const changedScopes = this.items.filter(item => {
        return Number(item.baseline_added_content_count || 0) > 0 &&
          Number(item.missing_current_count || 0) === 0 && Number(item.missing_previous_count || 0) === 0
      })
      if (!missingSnapshots.length && !changedScopes.length) return ''
      const allCurrentMissing = missingSnapshots.length === this.items.length && missingSnapshots.every(item => {
        return Number(item.expected_content_count || 0) > 0 && Number(item.observed_content_count || 0) === 0
      })
      if (allCurrentMissing) return `${this.date || '所选日期'} 运营数据尚未更新，变化暂不计算。`
      if (missingSnapshots.length && changedScopes.length) {
        return `${missingSnapshots.length} 个平台有作品缺少快照，另有 ${changedScopes.length} 个平台作品范围发生变化；已展示现有评论合计，变化暂不计算。`
      }
      if (missingSnapshots.length) return `${missingSnapshots.length} 个平台有作品缺少快照，已展示现有评论合计，变化暂不计算。`
      return `${changedScopes.length} 个平台作品范围发生变化，变化暂不计算。`
    },
    detailFilterHint () {
      const labels = {
        current_missing: `以下作品缺少 ${this.detailDate || '所选日期'} 的评论数，请检查该作品当天的运营数据更新记录。`,
        previous_missing: '以下作品当日已有数据，但前一日没有评论数，因此暂时无法计算完整变化。',
        baseline_added: '以下作品从所选日期开始纳入统计，因此无法与前一天直接比较。可核对发布时间和纳入监测时间。'
      }
      return labels[this.changeType] || ''
    }
  },
  methods: {
    async load (date, platform) {
      if (!date) return
      this.date = date
      this.platform = platform || ''
      this.loading = true
      this.$emit('loading-change', true)
      this.error = ''
      try {
        const result = await yuqingApi.get('creator-content/comment-statistics', {
          date: this.date,
          platform: this.platform || undefined
        })
        this.items = result.items || []
        this.previousDate = result.previous_date || ''
      } catch (error) {
        this.items = []
        this.error = 'load_failed'
      } finally {
        this.loading = false
        this.$emit('loading-change', false)
      }
    },
    openDetails (row) {
      if (!row) return
      this.detailDate = this.date
      this.detailPreviousDate = this.previousDate
      this.detailPlatform = row.platform
      const focusedTypes = {
        current_incomplete: 'current_missing',
        previous_incomplete: 'previous_missing',
        baseline_changed: 'baseline_added'
      }
      this.changeType = focusedTypes[row.calculation_status] || 'all'
      this.keyword = ''
      this.detailPage = 1
      this.drawerVisible = true
      this.loadDetails()
    },
    closeDrawer (done) {
      if (this.detailsLoading) return this.$message.info('作品明细正在加载，请稍候')
      done()
    },
    searchDetails () {
      if (!this.detailDate) return this.$message.warning('请选择统计日期')
      this.detailPage = 1
      this.loadDetails()
    },
    async loadDetails () {
      if (!this.detailDate) return
      this.detailsLoading = true
      this.detailsError = ''
      try {
        const result = await yuqingApi.get('creator-content/comment-statistics/details', {
          date: this.detailDate,
          platform: this.detailPlatform || undefined,
          change_type: this.changeType,
          keyword: this.keyword || undefined,
          page: this.detailPage,
          page_size: this.detailPageSize
        })
        this.details = result.items || []
        this.detailTotal = Number(result.total || 0)
        this.detailPreviousDate = result.previous_date || this.detailPreviousDate
      } catch (error) {
        this.details = []
        this.detailTotal = 0
        this.detailsError = 'load_failed'
      } finally {
        this.detailsLoading = false
      }
    },
    changeDetailPage (page) {
      this.detailPage = page
      this.loadDetails()
    },
    viewComments (row) {
      this.drawerVisible = false
      this.$emit('open-comments', row)
    },
    detailActionText (row) {
      const labels = {
        current_incomplete: `查看缺少数据的 ${this.number(row.missing_current_count)} 篇`,
        previous_incomplete: `查看缺少数据的 ${this.number(row.missing_previous_count)} 篇`,
        baseline_changed: '查看作品范围变化'
      }
      return labels[row.calculation_status] || '查看作品明细'
    },
    platformName (value) {
      const found = this.platforms.find(item => item.value === value)
      return found ? found.label : '全部平台'
    },
    coverageText (row) {
      if (row.calculation_status === 'no_data') return '没有纳入统计的作品'
      if (row.calculation_status === 'current_incomplete' && Number(row.observed_content_count || 0) === 0) {
        return `${this.number(row.expected_content_count)} 篇尚未更新`
      }
      return `已更新 ${this.number(row.observed_content_count)}/${this.number(row.expected_content_count)}`
    },
    coverageClass (row) {
      return ['complete', 'no_data'].includes(row.calculation_status) ? 'coverage-text' : 'warning-text'
    },
    statusNote (row) {
      if (row.calculation_status === 'current_incomplete') {
        return Number(row.observed_content_count || 0) === 0
          ? '运营数据更新后即可查看'
          : `今日还缺 ${this.number(row.missing_current_count)} 篇`
      }
      if (row.calculation_status === 'previous_incomplete') return `前一日缺 ${this.number(row.missing_previous_count)} 篇`
      if (row.calculation_status === 'baseline_changed') return `统计范围新增 ${this.number(row.baseline_added_content_count)} 篇`
      return ''
    },
    detailStatusType (row) {
      if (['current_missing', 'previous_missing'].includes(row.change_type)) return 'danger'
      if (['baseline_added', 'new_content'].includes(row.change_type)) return 'warning'
      return 'success'
    },
    detailStatusText (row) {
      const labels = {
        current_missing: '当日缺少数据',
        previous_missing: '前一日缺少数据',
        baseline_added: '统计范围新增',
        new_content: '当日新作品'
      }
      return labels[row.change_type] || '数据完整'
    },
    totalText (row, key) {
      if (row.calculation_status === 'no_data') return '—'
      if (key === 'current_total' && Number(row.expected_content_count || 0) > 0 && Number(row.observed_content_count || 0) === 0) return '—'
      if (key === 'previous_total' && Number(row.previous_expected_content_count || 0) > 0 && Number(row.previous_observed_content_count || 0) === 0) return '—'
      return this.number(row[key])
    },
    totalNote (row, key) {
      if (row.calculation_status === 'no_data') return ''
      if (key === 'current_total') {
        const missing = Number(row.missing_current_count || 0)
        if (!missing) return ''
        return Number(row.observed_content_count || 0) === 0
          ? '暂无可用快照'
          : `不含缺少快照的 ${this.number(missing)} 篇`
      }
      const missing = Number(row.missing_previous_count || 0)
      if (!missing) return ''
      return Number(row.previous_observed_content_count || 0) === 0
        ? '暂无可用快照'
        : `不含缺少快照的 ${this.number(missing)} 篇`
    },
    previousText (row) {
      if (row.change_type === 'new_content') return '0'
      return this.nullableNumber(row.previous_count)
    },
    nullableNumber (value) {
      return value === null || value === undefined ? '—' : this.number(value)
    },
    number (value) {
      const number = Number(value || 0)
      return Number.isFinite(number) ? number.toLocaleString('zh-CN') : '—'
    },
    signed (value) {
      const number = Number(value || 0)
      return `${number > 0 ? '+' : ''}${number.toLocaleString('zh-CN')}`
    },
    rate (value) {
      if (value === null || value === undefined) return ''
      const number = Number(value)
      return `${number > 0 ? '+' : ''}${(number * 100).toFixed(2)}%`
    },
    changeClass (value) {
      const number = Number(value || 0)
      return number > 0 ? 'change-up' : number < 0 ? 'change-down' : ''
    },
    formatDate (value) {
      return value ? String(value).replace('T', ' ').slice(0, 16) : '—'
    },
    shortDate (value) {
      if (!value) return '--'
      const parts = String(value).slice(0, 10).split('-')
      return parts.length === 3 ? `${parts[1]}-${parts[2]}` : String(value)
    },
    isURL (value) {
      try {
        const url = new URL(value)
        return url.protocol === 'http:' || url.protocol === 'https:'
      } catch (error) {
        return false
      }
    }
  }
}
</script>

<style scoped>
.statistics-card { margin-bottom: 16px }
.card-header { display: flex; align-items: center; justify-content: space-between; min-height: 38px }
.card-title { color: #303133; font-size: 16px; font-weight: 600 }
.card-description { color: #606266; font-size: 13px; margin-top: 6px }
.statistics-alert { margin-bottom: 14px }
.summary-table { width: 100% }
.drawer-body { padding: 0 20px 24px }
.detail-filters { padding: 14px 16px 0; margin-bottom: 14px; background: #f7f8fa; border-radius: 4px }
.details-table { width: 100% }
.muted { color: #909399 }
.source-id { margin-top: 4px; font-size: 12px }
.total-value { color: #303133; font-variant-numeric: tabular-nums }
.total-note { margin-top: 4px; color: #e6a23c; font-size: 12px; line-height: 1.3 }
.warning-text { color: #e6a23c; font-weight: 600 }
.coverage-text { color: #606266 }
.status-note { margin-top: 4px; color: #909399; font-size: 12px; line-height: 1.4 }
.change-up { color: #f56c6c; font-weight: 600 }
.change-down { color: #67c23a; font-weight: 600 }
.change-rate { margin-left: 5px; font-size: 12px; font-weight: 400 }
.pagination { display: flex; justify-content: flex-end; margin-top: 16px }
</style>
